import {
  Button,
  GridNavProvider,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TOUCH,
} from "@nymbl/nos2-rules";
import { ChevronRight } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { GridCell } from "@/components/time/GridCell";
import { GridTaskInfo, GroupPlan, HeldComments, heldInRow } from "@/components/time/GridTaskInfo";
import { ToggleAllProjects } from "@/components/time/ToggleAllProjects";
import { TypeChip } from "@/components/time/TypeChip";
import { cellKey, type GridRowHandlers, projectGroupName } from "@/components/time/WeekGridRow";
import { formatDayHeader, type IsoDate, weekdayShort } from "@/lib/time/dates";
import { defaultDayIndex, type GridModel, type GridRow, groupGridRows } from "@/lib/time/grid";
import { cellLabel } from "@/lib/time/labels";
import { formatHours } from "@/lib/time/rules";
import { type GridSortKey, type SortState, sortGridRows } from "@/lib/time/table-view";
import { cn } from "@/lib/utils";

type DaySort = "task" | "day" | "week";

const SORTS: { value: DaySort; label: string }[] = [
  { value: "task", label: "Task" },
  { value: "day", label: "This day" },
  { value: "week", label: "Week" },
];

const DAY_KEYS: readonly GridSortKey[] = ["day0", "day1", "day2", "day3", "day4", "day5", "day6"];

/** The GridSortKey a phone sort means for the selected day; hours sort most first. */
function sortState(choice: DaySort, day: number): SortState<GridSortKey> {
  if (choice === "task") return { key: "task", dir: "asc" };
  if (choice === "week") return { key: "week", dir: "desc" };
  return { key: DAY_KEYS[day] ?? "day0", dir: "desc" };
}

interface WeekGridDayProps extends GridRowHandlers {
  grid: GridModel;
  /** Filtered, unsorted rows; this layout sorts them itself. */
  rows: GridRow[];
  today: IsoDate;
  /** The day to open on; today when unset. */
  selectedDay?: IsoDate;
}

/**
 * The grid below md: a Sun-Sat day switcher and one card per task with that day's cell.
 * Same cells and saves as the desktop table; layout only.
 */
export function WeekGridDay({
  grid,
  rows,
  today,
  selectedDay,
  pending,
  resets,
  saving,
  favoritePending,
  onToggleFavorite,
  onCommit,
  onOpenEntries,
  collapsed,
  onToggleGroup,
  onSetGroups,
}: WeekGridDayProps) {
  const [day, setDay] = useState(() => defaultDayIndex(grid.days, selectedDay ?? today));
  const [sort, setSort] = useState<DaySort | null>(null);
  const shown = useMemo(
    () => (sort ? sortGridRows(rows, sortState(sort, day)) : rows),
    [rows, sort, day],
  );
  // Unsorted, the cards sit under their project, as on the desktop table.
  const groups = useMemo(() => (sort ? null : groupGridRows(rows)), [rows, sort]);
  const ptoHours = grid.pto?.cells[day]?.hours ?? 0;
  // The day tabs control the task list below (there is no TabsContent per day).
  const listId = useId();

  const order = new Map(
    (groups ? groups.flatMap((g) => g.rows) : shown).map((row, i) => [row.key, i]),
  );

  function renderRow(row: GridRow, planOnHeading = false) {
    const held = heldInRow(row, pending);
    const cell = row.cells[day];
    const p = cell ? pending[cell.key] : undefined;
    return (
      <li key={row.key} className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <GridTaskInfo
              row={row}
              held={held}
              favoritePending={favoritePending}
              onToggleFavorite={() => onToggleFavorite(row)}
              grouped={groups !== null}
              planOnHeading={planOnHeading}
            />
          </div>
          {cell && (
            <div className="shrink-0">
              <GridCell
                key={cellKey(cell, p, resets[cell.key] ?? 0)}
                cell={cell}
                row={order.get(row.key) ?? 0}
                col={0}
                label={cellLabel(row, cell.date)}
                pendingRaw={p ? p.raw : null}
                onCommit={(raw) => onCommit(row, cell, raw, null)}
                onOpenEntries={() => onOpenEntries(row, cell)}
              />
            </div>
          )}
        </div>
        <HeldComments
          held={held}
          saving={saving}
          idPrefix="day-"
          onSave={(x, comment) => onCommit(row, x.cell, x.p.raw, comment)}
        />
      </li>
    );
  }

  return (
    <div className="space-y-3">
      <Tabs value={String(day)} onValueChange={(v) => setDay(Number(v))}>
        <TabsList
          aria-label="Day"
          className="grid h-auto w-full grid-cols-7 group-data-[orientation=horizontal]/tabs:h-auto"
        >
          {grid.days.map((d, i) => (
            <TabsTrigger
              key={d}
              value={String(i)}
              aria-label={formatDayHeader(d)}
              aria-controls={listId}
              className="min-h-11 flex-col gap-0 px-0 py-1"
            >
              <span className="text-xs font-semibold">{weekdayShort(d).charAt(0)}</span>
              <span className={cn("text-sm font-semibold", d === today && "text-info")}>
                {Number(d.slice(8))}
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  "size-1.5 rounded-full",
                  (grid.dayTotals[i] ?? 0) > 0 ? "bg-info" : "bg-transparent",
                )}
              />
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex items-center justify-between gap-2">
        <Select value={sort ?? ""} onValueChange={(v) => setSort(v as DaySort)}>
          <SelectTrigger className={cn("w-40", TOUCH)} aria-label="Sort by">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ToggleAllProjects
          groups={groups ?? []}
          collapsed={collapsed}
          pending={pending}
          onSetAll={onSetGroups}
        />
      </div>

      <GridNavProvider>
        <ul id={listId} className="divide-y rounded-lg border bg-card" aria-label="Tasks">
          {grid.pto && (
            <li className="flex items-start justify-between gap-3 bg-muted/40 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{grid.pto.label}</span>
                <TypeChip type="pto" />
              </div>
              <div className="flex min-h-11 w-16 items-center justify-center text-sm font-semibold text-muted-foreground">
                {ptoHours > 0 ? formatHours(ptoHours) : "–"}
              </div>
            </li>
          )}
          {groups
            ? groups.flatMap((group) => {
                const open =
                  !collapsed.has(group.key) ||
                  group.rows.some((r) => r.cells.some((c) => pending[c.key]));
                const dayHours = group.dayTotals[day] ?? 0;
                return [
                  <li key={`group:${group.key}`} className="bg-muted/50 px-3 py-1">
                    <Button
                      variant="ghost"
                      onClick={() => onToggleGroup(group.key)}
                      aria-expanded={open}
                      aria-label={`${open ? "Collapse" : "Expand"} ${projectGroupName(group)}`}
                      className={cn(
                        TOUCH,
                        "h-auto w-full justify-start whitespace-normal text-left font-normal",
                      )}
                    >
                      <ChevronRight
                        className={cn(
                          "size-4 shrink-0 text-muted-foreground transition-transform",
                          open && "rotate-90",
                        )}
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-display text-sm font-bold break-words">
                          {group.projectName}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {[
                            group.accountName,
                            `${group.rows.length} ${group.rows.length === 1 ? "task" : "tasks"}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      <span className="text-sm font-semibold text-muted-foreground">
                        {dayHours > 0 ? formatHours(dayHours) : ""}
                      </span>
                    </Button>
                    {group.plan && <GroupPlan plan={group.plan} />}
                  </li>,
                  ...(open ? group.rows.map((row) => renderRow(row, group.plan !== null)) : []),
                ];
              })
            : shown.map((row) => renderRow(row))}
        </ul>
      </GridNavProvider>

      <div className="rounded-lg bg-muted/40 px-3 py-2 text-sm font-semibold">
        Day {formatHours(grid.dayTotals[day] ?? 0)} · Week {formatHours(grid.weekTotal)}
      </div>
    </div>
  );
}
